import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { X, Certificate } from "@phosphor-icons/react";

/**
 * SetQuoteModal — admin sets the price on an `awaiting_quote` project.
 * Calls POST /admin/projects/{id}/set-quote, which flips payment_status to unpaid
 * and emails the client the quote link.
 */
export function SetQuoteModal({ open, onClose, project, onSaved }) {
  const [total, setTotal] = useState("");
  const [deposit, setDeposit] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  if (!open || !project) return null;

  const totalNum = parseFloat(total);
  const depositNum = parseFloat(deposit || 0);
  const valid = Number.isFinite(totalNum) && totalNum > 0 && depositNum >= 0 && depositNum <= totalNum;

  const submit = async () => {
    if (!valid) return;
    setSaving(true);
    try {
      await api.post(`/admin/projects/${project.id}/set-quote`, {
        total_usd: totalNum,
        deposit_usd: depositNum || 0,
        quote_note: note.trim() || null,
      });
      toast.success("Quote sent to client.");
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to set quote");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="set-quote-modal"
    >
      <div
        className="bg-[#0a0a0a] border border-[#FF2D2D] max-w-lg w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <Certificate weight="fill" size={22} className="text-[#FF2D2D]" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF2D2D]">Set quote</div>
              <div className="font-display text-2xl mt-1">{project.title}</div>
              <div className="text-xs text-[#a3a3a3] mt-0.5">for {project.client_name || project.client_email}</div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="set-quote-close"
            className="text-[#a3a3a3] hover:text-white"
          >
            <X size={18} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mb-2">
              Total (USD) <span className="text-[#FF2D2D]">*</span>
            </label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              value={total}
              onChange={(e) => setTotal(e.target.value)}
              data-testid="set-quote-total"
              placeholder="0.00"
              className="w-full bg-[#050505] border border-[#262626] px-4 py-3 focus:border-[#FF2D2D] outline-none tabular-nums"
            />
          </div>

          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mb-2">
              Deposit due at approval (optional)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={deposit}
              onChange={(e) => setDeposit(e.target.value)}
              data-testid="set-quote-deposit"
              placeholder="0.00"
              className="w-full bg-[#050505] border border-[#262626] px-4 py-3 focus:border-[#FF2D2D] outline-none tabular-nums"
            />
            <div className="text-xs text-[#a3a3a3] mt-1">Leave blank if you want the client to pay in full up front.</div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-[#a3a3a3] mb-2">
              Scope note for the client (optional)
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="set-quote-note"
              rows={4}
              placeholder="e.g. Covers: 3 rounds of revisions, 2 sizes, final source files. Excludes: printing costs."
              className="w-full bg-[#050505] border border-[#262626] px-4 py-3 focus:border-[#FF2D2D] outline-none"
            />
          </div>

          <div className="text-xs text-[#a3a3a3] border-t border-[#262626] pt-3">
            The client will get an email with the quote, this note, and a link to review + pay in-portal.
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 mt-6 pt-4 border-t border-[#262626]">
          <button
            type="button"
            onClick={onClose}
            data-testid="set-quote-cancel"
            className="btn-outline px-5 py-2.5 text-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!valid || saving}
            data-testid="set-quote-submit"
            className="btn-primary px-6 py-2.5 text-sm font-bold uppercase tracking-wider disabled:opacity-50"
          >
            {saving ? "Sending…" : "Send quote →"}
          </button>
        </div>
      </div>
    </div>
  );
}
